import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useListingContext } from '../../../contexts/ListingContext';
import styles from '../../../styles/ListingManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import AddImageModal from './AddImageModal';
import AddListingDocumentModal from './AddListingDocumentModal';
import AddClientDocumentModal from './AddClientDocumentModal';
import ClientTile, { ClientForm, PROVINCES, MARITAL_STATUSES } from './ClientTile';
import AddClientModal from './AddClientModal';
import type {
  ListingType, ListingStatus, ListingDetail, ListingClient, MaritalStatus,
  ListingImageType, ListingDocumentType, ListingImage, ListingDocument,
  ListingClientDocumentType, ListingClientType,
} from '../../../types/ListingTypes';
import LoadingSpinner from 'components/common/LoadingSpinner';

const IMAGE_TYPES: ListingImageType[] = [
  'Bedroom', 'Bathroom', 'Kitchen', 'Patio', 'Garden', 'Front', 'Back', 'Scullery', 'Garage',
];

// ─── Listing form ─────────────────────────────────────────────────────────────

interface EditForm {
  complexName: string;
  unitNumber: string;
  streetNumber: string;
  streetName: string;
  city: string;
  province: string;
  postalCode: string;
  type: ListingType | '';
  status: ListingStatus;
}

const toListingForm = (d: ListingDetail): EditForm => ({
  complexName: d.complexName ?? '',
  unitNumber: d.unitNumber != null ? String(d.unitNumber) : '',
  streetNumber: String(d.streetNumber),
  streetName: d.streetName,
  city: d.city,
  province: d.province,
  postalCode: d.postalCode,
  type: d.type,
  status: d.status,
});

const toClientForm = (c: ListingClient): ClientForm => ({
  name: c.name,
  surname: c.surname,
  email: c.email,
  cell: c.cell,
  streetNumber: String(c.streetNumber ?? ''),
  streetName: c.streetName,
  city: c.city,
  province: c.province,
  postalCode: c.postalCode,
  maritalStatus: c.maritalStatus,
});

// ─── Component ────────────────────────────────────────────────────────────────

const EditListing: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    openListing, activeListing, clearActiveListing,
    updateListing, updateClient, deleteClient, createClient,
    uploadListingImage, deleteListingImage,
    uploadListingDocument, deleteListingDocument,
    fetchDocumentTypes, fetchListingTypes,
    fetchClientTypes, fetchClientDocumentTypes, uploadClientDocument, deleteClientDocument,
  } = useListingContext();

  const [form, setForm] = useState<EditForm | null>(null);
  const [clientForms, setClientForms] = useState<Record<string, ClientForm>>({});
  const [clientSaving, setClientSaving] = useState<Record<string, boolean>>({});
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [expandedClients, setExpandedClients] = useState<Record<string, boolean>>({});

  // Per-client document state
  const [clientDocTypes, setClientDocTypes] = useState<Record<string, ListingClientDocumentType[]>>({});
  const [newClientDocTypeId, setNewClientDocTypeId] = useState<Record<string, string>>({});
  const [newClientDocName, setNewClientDocName] = useState<Record<string, string>>({});
  const [newClientDocFile, setNewClientDocFile] = useState<Record<string, File | null>>({});
  const [clientDocUploading, setClientDocUploading] = useState<Record<string, boolean>>({});
  const [clientDocError, setClientDocError] = useState<Record<string, string | null>>({});

  // Images state
  const [images, setImages] = useState<ListingImage[]>([]);
  const [newImageTag, setNewImageTag] = useState<ListingImageType>('Bedroom');
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  // Documents state
  const [documents, setDocuments] = useState<ListingDocument[]>([]);
  const [documentTypes, setDocumentTypes] = useState<ListingDocumentType[]>([]);
  const [newDocTypeId, setNewDocTypeId] = useState('');
  const [newDocName, setNewDocName] = useState('');
  const [newDocFile, setNewDocFile] = useState<File | null>(null);
  const [docUploading, setDocUploading] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);

  // Client type options (for adding new clients)
  const [availableClientTypes, setAvailableClientTypes] = useState<ListingClientType[]>([]);
  const [addClientModalOpen, setAddClientModalOpen] = useState(false);
  const [newClientTypeId, setNewClientTypeId] = useState('');
  const [newClientForm, setNewClientForm] = useState<ClientForm>({
    name: '', surname: '', email: '', cell: '',
    streetNumber: '', streetName: '', city: '', province: '', postalCode: '', maritalStatus: '',
  });
  const [addClientSaving, setAddClientSaving] = useState(false);
  const [addClientError, setAddClientError] = useState<string | null>(null);

  // Modal state
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [clientDocModal, setClientDocModal] = useState<string | null>(null);
  const [pendingTypeChange, setPendingTypeChange] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setFetchError(null);
    openListing(id)
      .then(() => setLoading(false))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to load listing';
        setFetchError(msg);
        setLoading(false);
      });
    return () => clearActiveListing();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialise forms once the detail is available
  useEffect(() => {
    if (!activeListing) return;
    if (!form) setForm(toListingForm(activeListing));
    setImages(activeListing.images ?? []);
    setDocuments(activeListing.documents ?? []);
    setClientForms(prev => {
      const next = { ...prev };
      for (const c of activeListing.clients) {
        if (!next[c.id]) next[c.id] = toClientForm(c);
      }
      return next;
    });
    // Fetch document types filtered to this listing's type
    if (activeListing.type) {
      fetchListingTypes()
        .then(listingTypes => {
          const match = listingTypes.find(lt => lt.name === activeListing.type);
          return fetchDocumentTypes(match?.id);
        })
        .then(types => {
          setDocumentTypes(types);
          if (types.length > 0) setNewDocTypeId(types[0].id);
        })
        .catch(() => {});
    }
  }, [activeListing]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Listing form handlers ──────────────────────────────────────────────────

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'type' && activeListing && activeListing.clients.length > 0 && value !== form?.type) {
      setPendingTypeChange(value);
      return;
    }
    setForm(prev => prev ? { ...prev, [name]: value } : prev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !form) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await updateListing(id, {
        complexName: form.complexName || undefined,
        unitNumber: form.unitNumber ? Number(form.unitNumber) : undefined,
        streetNumber: Number(form.streetNumber),
        streetName: form.streetName,
        city: form.city,
        province: form.province,
        postalCode: form.postalCode,
        type: form.type as ListingType,
        status: form.status,
      });
      navigate(`/ListingViewer/${id}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save changes';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Client form handlers ───────────────────────────────────────────────────

  const handleClientChange = (clientId: string, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setClientForms(prev => ({ ...prev, [clientId]: { ...prev[clientId], [name]: value } }));
  };

  const handleSaveClient = async (clientId: string) => {
    const cf = clientForms[clientId];
    if (!cf) return;
    setClientSaving(prev => ({ ...prev, [clientId]: true }));
    setClientErrors(prev => ({ ...prev, [clientId]: '' }));
    try {
      await updateClient(clientId, {
        name: cf.name,
        surname: cf.surname,
        email: cf.email,
        cell: cf.cell,
        streetNumber: cf.streetNumber ? Number(cf.streetNumber) : undefined,
        streetName: cf.streetName,
        city: cf.city,
        province: cf.province,
        postalCode: cf.postalCode,
        maritalStatus: cf.maritalStatus as MaritalStatus,
      });
      setExpandedClients(prev => ({ ...prev, [clientId]: false }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save client';
      setClientErrors(prev => ({ ...prev, [clientId]: msg }));
    } finally {
      setClientSaving(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const toggleClient = (clientId: string) => {
    const willExpand = !expandedClients[clientId];
    setExpandedClients(prev => ({ ...prev, [clientId]: !prev[clientId] }));
    if (willExpand && !clientDocTypes[clientId]) {
      const client = activeListing?.clients.find(c => c.id === clientId);
      fetchClientDocumentTypes(client?.clientType?.id ?? undefined)
        .then(types => {
          setClientDocTypes(prev => ({ ...prev, [clientId]: types }));
          if (types.length > 0) setNewClientDocTypeId(prev => ({ ...prev, [clientId]: types[0].id }));
        })
        .catch(() => {});
    }
  };

  // ── Image handlers ─────────────────────────────────────────────────────────

  const handleUploadImage = async () => {
    if (!id || !newImageFile) return;
    setImageUploading(true);
    setImageError(null);
    try {
      await uploadListingImage(id, newImageTag, newImageFile);
      // Reload listing to get updated images with signed URLs
      await openListing(id);
      setNewImageFile(null);
      setImageModalOpen(false);
    } catch (err: unknown) {
      setImageError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    setImageError(null);
    try {
      await deleteListingImage(String(imageId));
      setImages(prev => prev.filter(img => img.id !== imageId));
    } catch (err: unknown) {
      setImageError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // ── Document handlers ──────────────────────────────────────────────────────

  const handleUploadDocument = async () => {
    if (!id || !newDocFile || !newDocTypeId || !newDocName.trim()) return;
    setDocUploading(true);
    setDocError(null);
    try {
      await uploadListingDocument(id, newDocTypeId, newDocName.trim(), newDocFile);
      await openListing(id);
      setNewDocName('');
      setNewDocFile(null);
      setDocModalOpen(false);
    } catch (err: unknown) {
      setDocError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setDocUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    setDocError(null);
    try {
      await deleteListingDocument(documentId);
      setDocuments(prev => prev.filter(d => d.id !== documentId));
    } catch (err: unknown) {
      setDocError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // ── Client document handlers ───────────────────────────────────────────────

  const handleUploadClientDoc = async (clientId: string) => {
    const file = newClientDocFile[clientId];
    const typeId = newClientDocTypeId[clientId];
    const name = (newClientDocName[clientId] ?? '').trim();
    if (!file || !typeId || !name || !id) return;
    setClientDocUploading(prev => ({ ...prev, [clientId]: true }));
    setClientDocError(prev => ({ ...prev, [clientId]: null }));
    try {
      await uploadClientDocument(clientId, typeId, name, file);
      setNewClientDocFile(prev => ({ ...prev, [clientId]: null }));
      setNewClientDocName(prev => ({ ...prev, [clientId]: '' }));
      await openListing(id);
      setClientDocModal(null);
    } catch (err: unknown) {
      setClientDocError(prev => ({ ...prev, [clientId]: err instanceof Error ? err.message : 'Upload failed' }));
    } finally {
      setClientDocUploading(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const handleDeleteClientDoc = async (clientId: string, docId: string) => {
    if (!id) return;
    setClientDocError(prev => ({ ...prev, [clientId]: null }));
    try {
      await deleteClientDocument(docId);
      await openListing(id);
    } catch (err: unknown) {
      setClientDocError(prev => ({ ...prev, [clientId]: err instanceof Error ? err.message : 'Delete failed' }));
    }
  };

  const handleRemoveClient = async (clientId: string) => {
    if (!id) return;
    try {
      await deleteClient(clientId);
      await openListing(id);
    } catch {
      // silent — client tile stays if removal fails
    }
  };

  const handleAddClient = async () => {
    if (!id) return;
    setAddClientSaving(true);
    setAddClientError(null);
    try {
      await createClient({
        listingId: id,
        clientType: newClientTypeId,
        name: newClientForm.name,
        surname: newClientForm.surname,
        email: newClientForm.email,
        cell: newClientForm.cell,
        streetNumber: Number(newClientForm.streetNumber) || 0,
        streetName: newClientForm.streetName,
        city: newClientForm.city,
        province: newClientForm.province,
        postalCode: newClientForm.postalCode,
        maritalStatus: newClientForm.maritalStatus as MaritalStatus,
      });
      await openListing(id);
      setAddClientModalOpen(false);
    } catch (err: unknown) {
      setAddClientError(err instanceof Error ? err.message : 'Failed to create client');
    } finally {
      setAddClientSaving(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const textField = (label: string, name: keyof EditForm, type: 'text' | 'number' = 'text', required = true) => (
    <div className={styles.categoryRow}>
      <label className={styles.categoryLabel}>
        {label}
        {required && <span style={{ color: '#dc3545', marginLeft: 2 }}>*</span>}
      </label>
      <div className={styles.categoryInputWrapper}>
        <input
          className={styles.categoryInput}
          type={type}
          name={name}
          value={form?.[name] ?? ''}
          onChange={handleChange}
          required={required}
          min={type === 'number' ? 0 : undefined}
        />
      </div>
    </div>
  );

  const clientTextField = (
    clientId: string,
    label: string,
    name: keyof ClientForm,
    type: 'text' | 'number' = 'text',
    required = true,
  ) => {
    const cf = clientForms[clientId];
    return (
      <div className={styles.categoryRow}>
        <label className={styles.categoryLabel}>
          {label}
          {required && <span style={{ color: '#dc3545', marginLeft: 2 }}>*</span>}
        </label>
        <div className={styles.categoryInputWrapper}>
          <input
            className={styles.categoryInput}
            type={type}
            name={name}
            value={cf?.[name] ?? ''}
            onChange={e => handleClientChange(clientId, e)}
            required={required}
            min={type === 'number' ? 0 : undefined}
          />
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  if (fetchError || !activeListing || !form) {
    return (
      <div className={styles.transactionManagementPage}>
        <p className={styles.transactionEmpty}>{fetchError ?? 'Listing not found.'}</p>
        <button
          className={`${buttonStyles.button} ${buttonStyles.secondary}`}
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <form className={styles.agreementForm} onSubmit={handleSubmit} noValidate>
      <h2 className={styles.agreementPageTitle}>Edit Listing</h2>

      {/* Listing Type */}
      <section className={`${styles.agreementFormSection} ${styles.agreementTypeSection}`}>
        <span className={styles.agreementTypeLabel}>Listing Type *</span>
        <select
          className={styles.agreementTypeSelect}
          name="type"
          value={form.type}
          onChange={handleChange}
          required
        >
          <option value="">— Select type —</option>
          <option value="Sale">Sale</option>
          <option value="Rental">Rental</option>
        </select>
      </section>

      {/* Property Details */}
      <section className={styles.agreementFormSection}>
        <h4>Property Details</h4>
        {textField('Complex Name', 'complexName', 'text', false)}
        {textField('Unit Number', 'unitNumber', 'number', false)}
        {textField('Street Number', 'streetNumber', 'number')}
        {textField('Street Name', 'streetName')}
        {textField('City', 'city')}

        <div className={styles.categoryRow}>
          <label className={styles.categoryLabel}>
            Province <span style={{ color: '#dc3545', marginLeft: 2 }}>*</span>
          </label>
          <div className={styles.categoryInputWrapper}>
            <select
              className={styles.categoryInput}
              name="province"
              value={form.province}
              onChange={handleChange}
              required
            >
              <option value="">— Select province —</option>
              {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        {textField('Postal Code', 'postalCode')}
      </section>

      {/* Status */}
      <section className={styles.agreementFormSection}>
        <h4>Status</h4>
        <div className={styles.categoryRow}>
          <label className={styles.categoryLabel}>Status</label>
          <div className={styles.categoryInputWrapper} style={{ width: 'fit-content' }}>
            <select
              className={styles.categoryInput}
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <option value="Open">Open</option>
              <option value="Pending">Pending</option>
              <option value="Incomplete">Incomplete</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
        </div>
      </section>

      {/* Clients */}
      <section className={styles.agreementFormSection}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h4 style={{ margin: 0 }}>Clients</h4>
          <button
            type="button"
            className={`${buttonStyles.button} ${buttonStyles.primary}`}
            onClick={async () => {
              if (!form?.type) return;
              if (availableClientTypes.length === 0) {
                const types = await fetchClientTypes({ listingTypeName: form.type });
                setAvailableClientTypes(types);
                if (types.length > 0) setNewClientTypeId(types[0].id);
              }
              setNewClientForm({ name: '', surname: '', email: '', cell: '', streetNumber: '', streetName: '', city: '', province: '', postalCode: '', maritalStatus: '' });
              setAddClientError(null);
              setAddClientModalOpen(true);
            }}
          >
            + Add Client
          </button>
        </div>
        {activeListing.clients.length === 0 && (
          <p className={styles.transactionEmpty} style={{ marginBottom: 16 }}>No clients added yet.</p>
        )}
        {activeListing.clients.length > 0 && (() => {
            const clientTypeNames = Array.from(new Set(
              activeListing.clients
                .map((c: ListingClient) => c.clientType?.name ?? '')
                .filter(Boolean)
            )).sort() as string[];
            const columns = clientTypeNames.length > 0 ? clientTypeNames : [''];

            const renderClientTile = (client: ListingClient) => {
              const cf = clientForms[client.id];
              if (!cf) return null;
              return (
                <ClientTile
                  key={client.id}
                  client={client}
                  form={cf}
                  expanded={expandedClients[client.id] ?? false}
                  saving={clientSaving[client.id] ?? false}
                  error={clientErrors[client.id]}
                  docError={clientDocError[client.id] ?? null}
                  onToggle={() => toggleClient(client.id)}
                  onFieldChange={e => handleClientChange(client.id, e)}
                  onSave={() => handleSaveClient(client.id)}
                  onDiscard={() => {
                    setClientForms(prev => ({ ...prev, [client.id]: toClientForm(client) }));
                    toggleClient(client.id);
                  }}
                  onDeleteDoc={docId => handleDeleteClientDoc(client.id, docId)}
                  onAddDoc={() => {
                    setClientDocError(prev => ({ ...prev, [client.id]: null }));
                    setClientDocModal(client.id);
                  }}
                  onRemove={() => handleRemoveClient(client.id)}
                />
              );
            };

            return (
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                {columns.map((type, idx) => (
                  <React.Fragment key={type}>
                    {idx > 0 && (
                      <div style={{ width: 1, alignSelf: 'stretch', background: '#dee2e6', margin: '0 20px', flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {type && (
                        <h5 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#6c757d', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>{type}</h5>
                      )}
                      <div style={{ width: 380 }}>
                        {activeListing.clients
                          .filter((c: ListingClient) => !type || c.clientType?.name === type)
                          .map(renderClientTile)}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            );
          })()}
      </section>

      {/* Images */}
      <section className={styles.agreementFormSection}>
        <h4>Images</h4>
        {images.length === 0 && (
          <p className={styles.transactionEmpty} style={{ marginBottom: 16 }}>No images uploaded yet.</p>
        )}
        <div className={styles.imageGrid} style={{ marginBottom: 20 }}>
          {images.map((img: ListingImage) => (
            <div key={img.id} className={styles.imageGridItem}>
              {img.url ? (
                <img src={img.url} alt={img.type} />
              ) : (
                <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd', fontSize: '0.85em' }}>
                  No preview
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px' }}>
                <span className={styles.imageGridItemLabel} style={{ padding: 0 }}>{img.type}</span>
                <button
                  type="button"
                  className={`${buttonStyles.button} ${buttonStyles.danger}`}
                  style={{ padding: '3px 10px', fontSize: '0.8em' }}
                  onClick={() => handleDeleteImage(img.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        {imageError && <p className={styles.agreementFormError}>{imageError}</p>}
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.primary}`}
          onClick={() => { setImageError(null); setImageModalOpen(true); }}
        >
          + Add Image
        </button>
      </section>

      {/* Documents */}
      <section className={styles.agreementFormSection}>
        <h4>Documents</h4>
        {documents.length === 0 && (
          <p className={styles.transactionEmpty} style={{ marginBottom: 16 }}>No documents uploaded yet.</p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {documents.map((doc: ListingDocument) => (
            <div key={doc.id} className={styles.docCard} style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div>
                  <strong>{doc.name}</strong>
                  {doc.documentType && (
                    <span style={{ marginLeft: 8, color: '#6c757d', fontSize: '0.85em' }}>
                      ({doc.documentType.name})
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  {doc.url && (
                    <a
                      href={doc.url}
                      download={doc.name}
                      className={styles.docDownloadBtn}
                      onClick={e => e.stopPropagation()}
                    >
                      ↓
                    </a>
                  )}
                  <button
                    type="button"
                    className={`${buttonStyles.button} ${buttonStyles.danger}`}
                    style={{ padding: '3px 10px', fontSize: '0.8em' }}
                    onClick={() => handleDeleteDocument(doc.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {docError && <p className={styles.agreementFormError}>{docError}</p>}
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.primary}`}
          onClick={() => { setDocError(null); setDocModalOpen(true); }}
        >
          + Add Document
        </button>
      </section>

      {/* Error & Actions */}
      {submitError && <p className={styles.agreementFormError}>{submitError}</p>}

      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <button
          type="submit"
          className={`${buttonStyles.button} ${buttonStyles.primary}`}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.secondary}`}
          onClick={() => navigate(`/ListingViewer/${id}`)}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>

      {/* ── Change Listing Type Confirmation ─────────────────────────── */}
      {pendingTypeChange !== null && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setPendingTypeChange(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 10, padding: 28, width: 420, maxWidth: '92vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <h4 style={{ margin: '0 0 10px', fontSize: '1.05rem', fontWeight: 700 }}>Change Listing Type?</h4>
            <p style={{ margin: '0 0 20px', color: '#495057', fontSize: '0.95rem', lineHeight: 1.5 }}>
              Changing the listing type will remove all <strong>{activeListing.clients.length} client{activeListing.clients.length !== 1 ? 's' : ''}</strong> currently attached to this listing. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className={`${buttonStyles.button} ${buttonStyles.secondary}`}
                onClick={() => setPendingTypeChange(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${buttonStyles.button} ${buttonStyles.danger}`}
                onClick={async () => {
                  await Promise.all(activeListing.clients.map((c: ListingClient) => deleteClient(c.id)));
                  setForm(prev => prev ? { ...prev, type: pendingTypeChange as ListingType } : prev);
                  setPendingTypeChange(null);
                  if (id) await openListing(id);
                }}
              >
                Change Type
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Client Modal ────────────────────────────────────────────── */}
      {addClientModalOpen && (
        <AddClientModal
          clientTypes={availableClientTypes}
          clientTypeId={newClientTypeId}
          onClientTypeChange={setNewClientTypeId}
          form={newClientForm}
          onFormChange={(field, value) => setNewClientForm(prev => ({ ...prev, [field]: value }))}
          saving={addClientSaving}
          error={addClientError}
          onClose={() => setAddClientModalOpen(false)}
          onSubmit={handleAddClient}
        />
      )}

      {/* ── Add Image Modal ─────────────────────────────────────────────── */}
      {imageModalOpen && (
        <AddImageModal
          onClose={() => setImageModalOpen(false)}
          imageTag={newImageTag}
          onImageTagChange={setNewImageTag}
          file={newImageFile}
          onFileChange={setNewImageFile}
          uploading={imageUploading}
          error={imageError}
          onUpload={handleUploadImage}
        />
      )}

      {/* ── Add Listing Document Modal ──────────────────────────────────── */}
      {docModalOpen && (
        <AddListingDocumentModal
          onClose={() => setDocModalOpen(false)}
          documentTypes={documentTypes}
          docTypeId={newDocTypeId}
          onDocTypeChange={setNewDocTypeId}
          name={newDocName}
          onNameChange={setNewDocName}
          file={newDocFile}
          onFileChange={setNewDocFile}
          uploading={docUploading}
          error={docError}
          onUpload={handleUploadDocument}
        />
      )}

      {/* ── Add Client Document Modal ───────────────────────────────────── */}
      {clientDocModal && (() => {
        const modalClient = activeListing.clients.find(c => c.id === clientDocModal);
        const cid = clientDocModal;
        return (
          <AddClientDocumentModal
            clientName={modalClient ? `${modalClient.name} ${modalClient.surname}` : ''}
            onClose={() => setClientDocModal(null)}
            documentTypes={clientDocTypes[cid] || []}
            docTypeId={newClientDocTypeId[cid] || ''}
            onDocTypeChange={val => setNewClientDocTypeId(prev => ({ ...prev, [cid]: val }))}
            name={newClientDocName[cid] || ''}
            onNameChange={val => setNewClientDocName(prev => ({ ...prev, [cid]: val }))}
            file={newClientDocFile[cid] ?? null}
            onFileChange={f => setNewClientDocFile(prev => ({ ...prev, [cid]: f }))}
            uploading={clientDocUploading[cid] ?? false}
            error={clientDocError[cid] ?? null}
            onUpload={() => handleUploadClientDoc(cid)}
          />
        );
      })()}
    </form>
  );
};

export default EditListing;